/**
 * Service Factory
 * 
 * Creates service instances based on environment configuration
 * following Module M7 (Integration Points) from the ConnectPath Modular Methodology.
 */

import { config } from '../../config/environment';
import { IAnalyticsService } from '../interfaces/IAnalyticsService';
import { IAssessmentService } from '../interfaces/IAssessmentService';
import { IAuthService } from '../interfaces/IAuthService';
import { IChurchService } from '../interfaces/IChurchService';
import { IDashboardService } from '../interfaces/IDashboardService';
import { IEngagementService } from '../interfaces/IEngagementService';
import { IReportingService } from '../interfaces/IReportingService';
import { IScheduleService } from '../interfaces/IScheduleService';
import { IMinistryService } from '../interfaces/IMinistryService';
import { IDistributionService } from '../interfaces/IDistributionService';
import { IMemberService } from '../interfaces/IMemberService';
import { IStrategicPlanningService } from '../interfaces/IStrategicPlanningService';
import { IAIService } from '../interfaces/IAIService';
import { IAIAgentService } from '../interfaces/IAIAgentService';

// Import API implementations
import { AnalyticsService } from '../implementations/AnalyticsService';
import { ApiAssessmentService } from '../implementations/api/ApiAssessmentService';
import { ApiAuthService } from '../implementations/api/ApiAuthService';
import { ApiEngagementService } from '../implementations/api/ApiEngagementService';
import { ApiChurchService } from '../implementations/api/ApiChurchService';
import { ApiMemberService } from '../implementations/api/ApiMemberService';
import { ApiStrategicPlanningService } from '../implementations/api/ApiStrategicPlanningService';
import { ApiAIService } from '../implementations/api/ApiAIService';
import { AIAgentService } from '../implementations/AIAgentService';
import { RegionalDataService } from '../implementations/RegionalDataService';
import { ApiDashboardService } from '../implementations/api/ApiDashboardService';
import { ApiReportingService } from '../implementations/api/ApiReportingService';
import { ApiScheduleService } from '../implementations/api/ApiScheduleService';
import { ApiDistributionService } from '../implementations/api/ApiDistributionService';
import { ApiMinistryService } from '../implementations/api/ApiMinistryService';

// Mock implementations removed - only using real API services for production

// Service instances cache
let analyticsService: IAnalyticsService | null = null;
let assessmentService: IAssessmentService | null = null;
let authService: IAuthService | null = null;
let churchService: IChurchService | null = null;
let dashboardService: IDashboardService | null = null;
let engagementService: IEngagementService | null = null;
let reportingService: IReportingService | null = null;
let scheduleService: IScheduleService | null = null;
let ministryService: IMinistryService | null = null;
let distributionService: IDistributionService | null = null;
let memberService: IMemberService | null = null;
let strategicPlanningService: IStrategicPlanningService | null = null;
let aiService: IAIService | null = null;
let aiAgentService: IAIAgentService | null = null;
let regionalDataService: RegionalDataService | null = null;

/**
 * Service Factory class following the Factory pattern
 */
export class ServiceFactory {
  /**
   * Get or create Analytics service instance
   */
  static getAnalyticsService(): IAnalyticsService {
    if (!analyticsService) {
      console.log('[ServiceFactory] Creating AnalyticsService (Real API)');
      analyticsService = new AnalyticsService(config.apiUrl);
      console.log('[ServiceFactory] Created: AnalyticsService (Real API)');
    }
    return analyticsService;
  }

  /**
   * Get or create Assessment service instance
   */
  static getAssessmentService(): IAssessmentService {
    if (!assessmentService) {
      console.log('[ServiceFactory] Creating AssessmentService (Real API)');
      assessmentService = new ApiAssessmentService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiAssessmentService (Real API)');
    }
    return assessmentService;
  }

  /**
   * Get or create Auth service instance
   */
  static getAuthService(): IAuthService {
    if (!authService) {
      console.log('[ServiceFactory] Creating AuthService (Real API)');
      authService = new ApiAuthService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiAuthService (Real API)');
    }
    return authService;
  }

  /**
   * Get or create Church service instance
   */
  static getChurchService(): IChurchService {
    if (!churchService) {
      console.log('[ServiceFactory] Creating ChurchService (Real API)');
      churchService = new ApiChurchService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiChurchService (Real API)');
    }
    return churchService;
  }

  /**
   * Get or create Dashboard service instance
   */
  static getDashboardService(): IDashboardService {
    if (!dashboardService) {
      console.log('[ServiceFactory] Creating DashboardService (Real API)');
      dashboardService = new ApiDashboardService();
      console.log('[ServiceFactory] Created: ApiDashboardService (Real API)');
    }
    return dashboardService;
  }

  /**
   * Get or create Engagement service instance
   */
  static getEngagementService(): IEngagementService {
    if (!engagementService) {
      console.log('[ServiceFactory] Creating EngagementService (Real API)');
      engagementService = new ApiEngagementService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiEngagementService (Real API)');
    }
    return engagementService;
  }

  /**
   * Get or create Reporting service instance
   */
  static getReportingService(): IReportingService {
    if (!reportingService) {
      console.log('[ServiceFactory] Creating ReportingService (Real API)');
      reportingService = new ApiReportingService();
      console.log('[ServiceFactory] Created: ApiReportingService (Real API)');
    }
    return reportingService;
  }

  /**
   * Get or create Schedule service instance
   */
  static getScheduleService(): IScheduleService {
    if (!scheduleService) {
      console.log('[ServiceFactory] Creating ScheduleService (Real API)');
      scheduleService = new ApiScheduleService();
      console.log('[ServiceFactory] Created: ApiScheduleService (Real API)');
    }
    return scheduleService;
  }

  /**
   * Get or create Ministry service instance
   */
  static getMinistryService(): IMinistryService {
    if (!ministryService) {
      console.log('[ServiceFactory] Creating MinistryService (Real API)');
      ministryService = new ApiMinistryService();
      console.log('[ServiceFactory] Created: ApiMinistryService (Real API)');
    }
    return ministryService;
  }

  /**
   * Get or create Distribution service instance
   */
  static getDistributionService(): IDistributionService {
    if (!distributionService) {
      console.log('[ServiceFactory] Creating DistributionService (Real API)');
      distributionService = new ApiDistributionService();
      console.log('[ServiceFactory] Created: ApiDistributionService (Real API)');
    }
    return distributionService;
  }

  /**
   * Get or create Member service instance
   */
  static getMemberService(): IMemberService {
    if (!memberService) {
      console.log('[ServiceFactory] Creating MemberService (Real API)');
      memberService = new ApiMemberService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiMemberService (Real API)');
    }
    return memberService;
  }

  /**
   * Get or create Strategic Planning service instance
   */
  static getStrategicPlanningService(): IStrategicPlanningService {
    if (!strategicPlanningService) {
      console.log('[ServiceFactory] Creating StrategicPlanningService (Real API)');
      strategicPlanningService = new ApiStrategicPlanningService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiStrategicPlanningService (Real API)');
    }
    return strategicPlanningService;
  }

  /**
   * Get or create AI service instance
   */
  static getAIService(): IAIService {
    if (!aiService) {
      console.log('[ServiceFactory] Creating AIService, useMockData:', config.useMockData);
      // For now, use API service even in mock mode
      aiService = new ApiAIService(config.apiUrl);
      console.log('[ServiceFactory] Created: ApiAIService (Real API) - No mock available');
    }
    return aiService;
  }

  /**
   * Get or create AI Agent service instance
   */
  static getAIAgentService(): IAIAgentService {
    if (!aiAgentService) {
      console.log('[ServiceFactory] Creating AIAgentService (Real API)');
      aiAgentService = new AIAgentService(config.apiUrl);
      console.log('[ServiceFactory] Created: AIAgentService (Real API)');
    }
    return aiAgentService;
  }

  /**
   * Get or create Export service instance (uses Analytics service)
   */
  static getExportService(): IAnalyticsService {
    // Export functionality is part of analytics service
    return this.getAnalyticsService();
  }

  /**
   * Get or create Regional Data service instance
   */
  static getRegionalDataService(): RegionalDataService {
    if (!regionalDataService) {
      console.log('[ServiceFactory] Creating RegionalDataService (Real API)');
      regionalDataService = new RegionalDataService(config.apiUrl);
      console.log('[ServiceFactory] Created: RegionalDataService (Real API)');
    }
    return regionalDataService;
  }

  /**
   * Reset all service instances (useful for testing)
   */
  static resetAll(): void {
    analyticsService = null;
    assessmentService = null;
    authService = null;
    churchService = null;
    dashboardService = null;
    engagementService = null;
    reportingService = null;
    scheduleService = null;
    ministryService = null;
    distributionService = null;
    memberService = null;
    strategicPlanningService = null;
    aiService = null;
    aiAgentService = null;
    regionalDataService = null;
  }

  /**
   * Reset all service instances to force reconnection
   */
  static reconfigure(): void {
    this.resetAll();
  }

  /**
   * Log current service configuration
   */
  static logConfiguration(): void {
    console.log('[ServiceFactory] Current Configuration:');
    console.log('- apiUrl:', config.apiUrl);
    console.log('- environment:', config.environment);
    console.log('- Mode: Production (Real API only)');
  }
}